/**
 * Preparing a project from the UI — KAPL "Engg process flow" (26-Sep-2026):
 *
 *   1 Requirement gathering → template
 *   2 Control project & room master — display & approve
 *   3 Constants required for calculation, based on the requirement — fixing / locking
 *   4 Calculation engine           (HVAC Design, calculation runs)
 *   5 CAD → BOQ                    (the BOQ further down this page)
 *
 * One strip across the top of the project page, each step's state read from
 * the server (project_setup.setup_status), so the page says where the project
 * really is and opens on what is next.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useLive } from "./live";
import { setActiveProject } from "./activeProject";
import { RoomInputsGrid } from "./HVACInputs";
import { useRanges, saveWithReason, RangeOverrides } from "./RangeField";
import { useInputScope, ScopeMark } from "./inputScope";
import { StructuredValue, KeyValueEditor, parseKeyValues, summarize, isTable } from "./StructuredValue";

const KZ = { blue: "#1F4E79", green: "#10B981", amber: "#F59E0B", red: "#DC2626",
             charcoal: "#111827", grey: "#6B7280" };
const lbl = { fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase",
              color: "#9CA3AF", display: "block", marginBottom: 4 };
const inp = { width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 13,
              border: "1px solid #E5E7EB", borderRadius: 7, fontFamily: "inherit", background: "#fff" };
const th = { padding: "6px 8px", fontWeight: 700, fontSize: 11, color: "#6B7280",
             borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap" };
const td = { padding: "5px 8px", fontSize: 12.5, borderBottom: "1px solid #F3F4F6",
             fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
// A table-valued constant (ISO class → ACH) reads as a summary, never as JSON.
const show = (v) => v === null || v === undefined || v === "" ? "—" : summarize(v);

const STATE = {
  DONE: ["✓", "#166534", "#F0FDF4", "#BBF7D0"],
  CHANGED: ["!", "#92400E", "#FFFBEB", "#FDE68A"],
  IN_PROGRESS: ["…", "#1E40AF", "#EFF6FF", "#BFDBFE"],
  TODO: ["○", "#4B5563", "#F9FAFB", "#E5E7EB"],
};

function Button({ children, onClick, disabled, tone = "blue", outline }) {
  const c = { blue: "#2563EB", green: "#059669", red: "#DC2626", grey: "#6B7280" }[tone];
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ padding: "6px 14px", fontSize: 12.5, fontWeight: 600, borderRadius: 7,
               whiteSpace: "nowrap", cursor: disabled ? "not-allowed" : "pointer",
               border: `1.5px solid ${disabled ? "#E5E7EB" : c}`,
               background: disabled ? "#F3F4F6" : outline ? "#fff" : c,
               color: disabled ? "#9CA3AF" : outline ? c : "#fff" }}>{children}</button>
  );
}

function Note({ tone = "amber", children }) {
  const c = { amber: ["#FFFBEB", "#92400E", "#FDE68A"], red: ["#FEF2F2", "#991B1B", "#FECACA"],
              green: ["#F0FDF4", "#166534", "#BBF7D0"], grey: ["#F9FAFB", "#4B5563", "#E5E7EB"] }[tone];
  return <div style={{ background: c[0], color: c[1], border: `1px solid ${c[2]}`, borderRadius: 8,
                       padding: "8px 12px", fontSize: 12.5, marginBottom: 10 }}>{children}</div>;
}

async function call(api, authFetch, path, method = "GET", body) {
  const r = await authFetch(`${api}/api/eng${path}`, {
    method, headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : `Request failed (${r.status}).`);
  return d;
}

// ── 1 Requirements ────────────────────────────────────────────────────────────
export function Requirements({ api, authFetch, pid, canEdit: canEditRole, frozen, onSaved }) {
  const canEdit = canEditRole && !frozen;
  const [d, setD] = useState(null);
  const [vals, setVals] = useState({});
  const [scope, setScope] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => call(api, authFetch, `/projects/${pid}/requirements`).then(x => {
    setD(x); setVals(x.values || {});
    setScope(Object.fromEntries((x.scope || []).map(s => [s.key, s.status])));
  }).catch(e => setErr(e.message)), [api, authFetch, pid]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div style={{ color: KZ.grey, fontSize: 13 }}>{err || "Loading…"}</div>;

  // Which fields apply follows what is being typed, the same rule as the server.
  const sc = Object.keys(scope).filter(k => scope[k] !== "EXCLUDED");
  const hvac = sc.includes("HVAC") || sc.includes("DUCT");
  const dx = hvac && ["VRF", "DUCTABLE_SPLIT", "DX_AHU", "PACKAGED", "COMFORT"].includes(vals.system_category);
  const appliesWhen = (w) => !w || (w === "hvac" ? hvac : w === "dx" ? dx
    : w === "power" ? (sc.includes("ELEC") || dx)
    : w === "classified" ? (vals.classification_mode && vals.classification_mode !== "UNCLASSIFIED") : true);
  // The input matrix of the system being chosen decides what else is asked
  // (KAPL ENG VRF IN 001 for VRV / VRF): the server sends each field's
  // treatment per offered system, so the form follows a change of system
  // before it is saved. A field it hides keeps its stored value.
  const fsAll = (d.field_scope || {})[vals.system_category || ""] || null;
  const fs = (k) => (fsAll && fsAll[k]) || null;
  const applies = (f) => appliesWhen(f.when) && (!fs(f.key) || fs(f.key).show);
  const isReq = (f) => (fs(f.key) ? fs(f.key).required : f.required);
  const matrixOn = !!(fsAll && Object.values(fsAll).some(x => x.state !== "UNCHANGED"));
  const mark = (f) => {
    const x = fs(f.key);
    if (!matrixOn || !x || !["M", "C", "O", "A"].includes(x.state)) return null;
    return <ScopeMark sc={{ applies: true, code: () => x.state, help: () => [x.help, x.why].filter(Boolean).join("\n") }} rec="requirements" k={f.key} />;
  };

  const save = async () => {
    setBusy(true); setErr("");
    try { const x = await call(api, authFetch, `/projects/${pid}/requirements`, "PUT", { values: vals, scope });
          setD(x); onSaved(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const modeNote = (d.classification_modes || []).find(m => m.key === vals.classification_mode);

  return (
    <div>
      {frozen && <Note tone="grey">Approved and frozen — open a revision on stage 2 to change these.</Note>}
      {d.missing.length > 0 && <Note>Still needed: {d.missing.join(", ")}.</Note>}
      {d.revision != null && d.missing.length === 0 &&
        <Note tone="green">Saved as revision {d.revision} by {d.saved_by} on {d.saved_at}.</Note>}
      {matrixOn && <Note tone="grey">Input matrix for {vals.system_category}: only the fields it covers are asked
        (M mandatory · C conditional · O optional — hover for the matrix's guidance). Classification, refrigerant,
        standards and the other fields it does not contain are hidden; any value already stored is kept.</Note>}
      {d.schema.filter(sec => !matrixOn || sec.key === "scope" || sec.fields.some(applies)).map(sec => (
        <div key={sec.key} style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: KZ.blue, marginBottom: 8 }}>{sec.title}</div>
          {sec.key === "scope" ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 8 }}>
              {d.scope.map(s => (
                <label key={s.key} style={{ fontSize: 12.5, border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px" }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{s.name}</div>
                  <select disabled={!canEdit} value={scope[s.key] || "EXCLUDED"}
                    onChange={e => setScope({ ...scope, [s.key]: e.target.value })} style={inp}>
                    <option value="REQUIRED">Required</option>
                    <option value="OPTIONAL">Optional</option>
                    <option value="EXCLUDED">Excluded</option>
                  </select>
                </label>
              ))}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 10 }}>
              {sec.fields.filter(applies).map(f => (
                <label key={f.key} style={{ gridColumn: f.kind === "textarea" ? "1 / -1" : undefined }}
                  title={[f.source ? `Source: ${f.source}` : null, fs(f.key) && matrixOn ? fs(f.key).help : null].filter(Boolean).join("\n") || undefined}>
                  <span style={lbl}>{f.label}{f.unit ? ` (${f.unit})` : ""}{isReq(f) ? " *" : ""}{mark(f)}</span>
                  {f.kind === "choice" ? (
                    <select disabled={!canEdit} value={vals[f.key] ?? ""} style={inp}
                      onChange={e => setVals({ ...vals, [f.key]: e.target.value || null })}>
                      <option value="">—</option>
                      {f.options.map(o => <option key={o} value={o}>
                        {f.key === "classification_mode"
                          ? ((d.classification_modes.find(m => m.key === o) || {}).label || o)
                          : String(o).replace(/_/g, " ")}</option>)}
                    </select>
                  ) : f.kind === "textarea" ? (
                    <textarea disabled={!canEdit} rows={2} value={vals[f.key] ?? ""} style={inp}
                      onChange={e => setVals({ ...vals, [f.key]: e.target.value })} />
                  ) : (
                    <input disabled={!canEdit} type={f.kind === "number" ? "number" : "text"} step="any"
                      value={vals[f.key] ?? ""} style={inp}
                      onChange={e => setVals({ ...vals, [f.key]: e.target.value })} />
                  )}
                  {f.help && <span style={{ fontSize: 11, color: KZ.grey }}>{f.help}</span>}
                </label>
              ))}
            </div>
          )}
          {sec.key === "classification" && modeNote &&
            <div style={{ fontSize: 11.5, color: KZ.grey, marginTop: 6 }}>{modeNote.note}</div>}
          {sec.key === "documents" &&
            <div style={{ fontSize: 11.5, color: KZ.grey, marginTop: 6 }}>
              Registered documents: {Object.keys(d.documents).length
                ? Object.entries(d.documents).map(([t, n]) => `${n} ${t}`).join(", ")
                : "none yet — attach the URS, drawings and specifications under Documents"}.
            </div>}
        </div>
      ))}
      {err && <Note tone="red">{err}</Note>}
      {canEdit && <Button onClick={save} disabled={busy}>{busy ? "Saving…" : "Save requirements"}</Button>}
    </div>
  );
}

// ── 2 Review: extracted values ────────────────────────────────────────────────
const FIELD_LABEL = (f) => f.replace(/_/g, " ").replace(/\bpct\b/, "%").replace(/\bm2\b/, "m²");

export function RegisterReview({ api, authFetch, pid, canEdit: canEditRole, frozen, onChanged }) {
  const canEdit = canEditRole && !frozen;
  const [r, setR] = useState(null);
  const [edits, setEdits] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const load = useCallback(() => call(api, authFetch, `/projects/${pid}/requirements/register`).then(setR)
    .catch(e => setErr(e.message)), [api, authFetch, pid]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_requirement_values"], load);
  if (!r) return <div style={{ color: KZ.grey, fontSize: 13 }}>{err || "Loading…"}</div>;
  const decide = async (decisions) => {
    setBusy(true); setErr("");
    try { setR(await call(api, authFetch, `/projects/${pid}/requirements/register/decide`, "POST", { decisions })); setEdits({}); onChanged && onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const acceptAll = async () => {
    setBusy(true); setErr("");
    try { setR(await call(api, authFetch, `/projects/${pid}/requirements/register/accept-all`, "POST", {})); onChanged && onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const pending = r.values.filter(v => v.status === "PROPOSED");
  const done = r.values.filter(v => v.status !== "PROPOSED");
  const groups = [...new Set(pending.map(v => v.room_no))].sort((a, b) => (a === "PROJECT" ? -1 : b === "PROJECT" ? 1 : a.localeCompare(b)));
  return (
    <div>
      {r.not_asked && r.not_asked.count > 0 && <Note tone="grey">{r.not_asked.count} value(s) read from documents are
        outside this {r.not_asked.system_category} project's input matrix ({Object.keys(r.not_asked.by_field).map(FIELD_LABEL).join(", ")})
        — not asked for, and not applied. They stay on record.</Note>}
      {r.values.length === 0 && <Note tone="grey">Nothing extracted yet. On stage 1, open Documents, read a document and choose "Extract into the requirements for review".</Note>}
      {pending.length > 0 && <Note>{pending.length} value(s) read from documents are waiting for you{r.conflicts ? ` — ${r.conflicts} where documents disagree; choose one` : ""}. Each shows the page and the document's own words; accept it as read, correct it, or reject it.</Note>}
      {err && <Note tone="red">{err}</Note>}
      {canEdit && pending.length > 0 && (
        <div style={{ marginBottom: 10 }}><Button tone="green" disabled={busy} onClick={acceptAll}>Accept all that do not conflict</Button></div>)}
      {groups.map(g => (
        <div key={g} style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: KZ.blue, marginBottom: 4 }}>
            {g === "PROJECT" ? "Project-wide" : `Room ${g}`}{pending.find(v => v.room_no === g && v.new_room) ? " (new room)" : ""}</div>
          <div style={{ overflowX: "auto", border: "1px solid #E5E7EB", borderRadius: 8 }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead><tr>{["Field", "Read", "Project now", "Source", "Document's words", ""].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>{pending.filter(v => v.room_no === g).map(v => (
                <tr key={v.id} style={{ background: v.conflict ? "#FFFBEB" : undefined }}>
                  <td style={{ ...td, fontWeight: 600 }}>{FIELD_LABEL(v.field)}{v.conflict && <div style={{ fontSize: 10.5, color: "#B45309" }}>documents disagree</div>}</td>
                  <td style={td}>{canEdit
                    ? <input value={edits[v.id] ?? show(v.value)} onChange={e => setEdits({ ...edits, [v.id]: e.target.value })} style={{ ...inp, width: 110, padding: "3px 6px" }} />
                    : show(v.value)} <span style={{ color: KZ.grey, fontSize: 11 }}>{v.unit || ""}</span>
                    {v.conversion && <div style={{ fontSize: 10.5, color: KZ.grey }}>{v.conversion}</div>}</td>
                  <td style={{ ...td, color: v.same_as_current ? KZ.green : KZ.grey }}>{show(v.current)}</td>
                  <td style={{ ...td, fontSize: 11.5, whiteSpace: "normal", minWidth: 120 }}>{v.document}
                    <div style={{ color: KZ.grey }}>{v.locator || v.source_ref}{v.page != null && !String(v.locator || v.source_ref || "").startsWith(`p.${v.page}`) ? ` (p.${v.page})` : ""} · {v.read_by}{v.confidence != null ? ` · ${Math.round(v.confidence * 100)}%` : ""}</div>
                    {v.model && <div style={{ color: KZ.grey, fontSize: 10.5 }}>read by {v.model}{v.provider === "groq" ? " on Groq" : ""}</div>}
                    {v.read_by === "VISION" && v.provider === "groq" && <div style={{ fontSize: 10.5, color: "#B45309" }}>Read off a picture by Qwen, which has invented rooms on drawings — check it against the page.</div>}
                    {v.vrf_path && <div style={{ fontSize: 10.5, color: "#B45309" }} title="A VRF input: written only when you accept it">{v.review_status} · VRF {v.vrf_path}</div>}</td>
                  <td style={{ ...td, fontSize: 11.5, whiteSpace: "normal", maxWidth: 280, color: "#374151", fontStyle: "italic" }}>{v.quoted ? `“${v.quoted}”` : "—"}</td>
                  <td style={td}>{canEdit && <>
                    <button disabled={busy} onClick={() => decide([{ id: v.id, action: "accept", value: edits[v.id] }])}
                      style={{ fontSize: 11.5, border: "none", background: "none", color: "#059669", cursor: "pointer", fontWeight: 700 }}>{edits[v.id] !== undefined && edits[v.id] !== show(v.value) ? "Accept corrected" : "Accept"}</button>
                    <button disabled={busy} onClick={() => decide([{ id: v.id, action: "reject" }])}
                      style={{ fontSize: 11.5, border: "none", background: "none", color: "#DC2626", cursor: "pointer" }}>Reject</button></>}</td>
                </tr>))}</tbody>
            </table>
          </div>
        </div>))}
      {done.length > 0 && (
        <div>
          <button onClick={() => setShowDone(x => !x)} style={{ fontSize: 12, border: "none", background: "none", color: KZ.blue, cursor: "pointer", padding: 0 }}>
            {showDone ? "▲" : "▼"} {r.accepted} accepted · {r.rejected} rejected</button>
          {showDone && <div style={{ marginTop: 6, fontSize: 12 }}>{done.map(v => (
            <div key={v.id} style={{ padding: "2px 0", color: v.status === "ACCEPTED" ? "#166534" : "#9CA3AF" }}>
              {v.status === "ACCEPTED" ? "✓" : "✕"} {v.room_no === "PROJECT" ? "Project" : v.room_no} · {FIELD_LABEL(v.field)} = {show(v.value)}
              {v.edited_from != null ? ` (corrected from ${show(v.edited_from)})` : ""} — {v.decided_by}</div>))}</div>}
        </div>)}
    </div>
  );
}

// ── 2 Review: rooms — edit, add, delete ───────────────────────────────────────
export function RoomsEditor({ api, authFetch, pid, canEdit: canEditRole, frozen, onChanged, onGoTo, scopeTick }) {
  const canEdit = canEditRole && !frozen;
  const [rooms, setRooms] = useState(null);
  const [method, setMethod] = useState(null);
  const [filters, setFilters] = useState([]);
  const [err, setErr] = useState("");
  const [add, setAdd] = useState(null);
  const [del, setDel] = useState("");
  // The project's input matrix (KAPL ENG VRF IN 001 on a VRV / VRF project)
  // sets the room master's columns. Such a project's rows are its room
  // records (the VRF heat-load view carries results, not the room fields).
  const sc = useInputScope(api, authFetch, pid, scopeTick, false);
  const load = useCallback(async () => {
    if (!sc.loaded) return;
    try {
      if (sc.applies) {
        const [t, b] = await Promise.all([call(api, authFetch, `/projects/${pid}`),
                                          call(api, authFetch, `/projects/${pid}/design-conditions`)]);
        const rows = [];
        (t.buildings || []).forEach(bl => (bl.floors || []).forEach(fl => (fl.zones || []).forEach(z =>
          (z.rooms || []).forEach(rm => rows.push({
            room_id: rm.id, room: rm.name, room_no: rm.room_no, zone: z.name,
            inputs: { ...(rm.design || {}), ...rm },
            volume_m3: rm.area_m2 && rm.height_m ? rm.area_m2 * rm.height_m : null })))));
        setRooms(rows); setMethod(b.heat_load_method); setFilters([]);
        return;
      }
      const [h, b] = await Promise.all([call(api, authFetch, `/projects/${pid}/hvac`),
                                        call(api, authFetch, `/projects/${pid}/design-conditions`)]);
      setRooms(h.rooms || []); setMethod(b.heat_load_method); setFilters(h.filter_catalogue || []);
    } catch (e) { setErr(e.message); setRooms([]); }
  }, [api, authFetch, pid, sc.loaded, sc.applies]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_rooms", "eng_room_designs", "eng_zones"], load);
  const ranges = useRanges(api, authFetch);
  const [rangeTick, setRangeTick] = useState(0);
  const saveRoom = async (roomId, field, raw) => {
    const text = ["cleanroom_class", "gmp_grade", "zone_name", "airflow_pattern",
                  "hepa_model", "hepa_mode"].includes(field);
    const value = raw === "" ? null : (text ? raw : Number(raw));
    setErr("");
    try {
      // Outside its allowed range: ask why, and send it as an override.
      await saveWithReason(reason => authFetch(`${api}/api/eng/rooms/${roomId}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reason ? { [field]: value, override_reason: reason } : { [field]: value }) }));
      await load(); setRangeTick(t => t + 1); onChanged && onChanged();
    }
    catch (e) { setErr(e.message); await load(); }
  };
  const create = async () => {
    setErr("");
    try {
      await call(api, authFetch, `/projects/${pid}/rooms`, "POST", {
        room_no: add.room_no || null, name: add.name, zone: add.zone || "General",
        height_m: add.height_m ? Number(add.height_m) : null });
      setAdd(null); await load(); onChanged && onChanged();
    } catch (e) { setErr(e.message); }
  };
  const remove = async () => {
    const r = rooms.find(x => String(x.room_id) === del);
    if (!r || !window.confirm(`Delete room ${r.room_no || ""} ${r.room || ""} from the room master?`)) return;
    setErr("");
    try { await call(api, authFetch, `/rooms/${del}`, "DELETE"); setDel(""); await load(); onChanged && onChanged(); }
    catch (e) { setErr(e.message); }
  };
  if (!rooms) return <div style={{ color: KZ.grey, fontSize: 13 }}>Loading rooms…</div>;
  return (
    <div>
      {err && <Note tone="red">{err}</Note>}
      <RoomInputsGrid rooms={rooms} editable={canEdit} onSave={saveRoom} method={method} filters={filters}
        ranges={ranges.rows} scope={sc} />
      <div style={{ marginTop: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: KZ.blue, marginBottom: 4 }}>Values outside their allowed range</div>
        <RangeOverrides api={api} authFetch={authFetch} projectId={pid} tick={rangeTick} onChanged={onChanged} />
      </div>
      {canEdit && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end", marginTop: 10 }}>
          {!add ? <Button outline onClick={() => setAdd({ room_no: "", name: "", zone: "", height_m: "" })}>+ Add room</Button> : (
            <>
              {[["room_no", "Room no"], ["name", "Name"], ["zone", "Zone / AHU"], ["height_m", "Height m"]].map(([k, l]) => (
                <label key={k} style={{ width: 130 }}><span style={lbl}>{l}</span>
                  <input value={add[k]} onChange={e => setAdd({ ...add, [k]: e.target.value })} style={inp} /></label>))}
              <Button disabled={!add.name} onClick={create}>Add</Button>
              <Button outline tone="grey" onClick={() => setAdd(null)}>Cancel</Button>
            </>)}
          <div style={{ flex: 1 }} />
          <select value={del} onChange={e => setDel(e.target.value)} style={{ ...inp, width: 200 }}>
            <option value="">Delete a room…</option>
            {rooms.filter(r => r.room_id).map(r => <option key={r.room_id} value={r.room_id}>{r.room_no ? `${r.room_no} · ` : ""}{r.room}</option>)}
          </select>
          <Button outline tone="red" disabled={!del} onClick={remove}>Delete</Button>
          <Button outline onClick={() => onGoTo && onGoTo("Room Builder")}>Draw in Room Builder</Button>
        </div>)}
    </div>
  );
}

// ── 2 Approve & freeze ────────────────────────────────────────────────────────
export function ApproveRequirements({ api, authFetch, pid, onChanged }) {
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState("");
  const load = useCallback(() => call(api, authFetch, `/projects/${pid}/room-master`).then(setM)
    .catch(e => setErr(e.message)), [api, authFetch, pid]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_rooms", "eng_room_designs", "eng_zones", "eng_projects", "eng_requirement_values"], load);
  if (!m) return null;
  const act = async (path, body) => {
    setBusy(true); setErr("");
    try { setM(await call(api, authFetch, path, "POST", body)); setWhy(""); onChanged && onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const blocks = m.checks.filter(c => c.severity === "BLOCK");
  return (
    <div>
      {m.frozen && m.state === "APPROVED" && <Note tone="green">Approved and frozen — revision {m.revision}, by {m.approved_by} on {m.approved_at}. Every calculation stands on this baseline.</Note>}
      {m.state === "CHANGED" && <Note>Changed since revision {m.revision} was approved:
        <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>{m.changes_since_approval.slice(0, 12).map(c => <li key={c}>{c}</li>)}</ul></Note>}
      {!m.frozen && m.revision && m.state !== "CHANGED" && <Note tone="grey">Revision open — make the changes, then approve again to freeze revision {m.revision + 1}.</Note>}
      {!m.classification_mode && <Note>State the classification mode in the requirements — it decides whether each room needs an ISO class, a GMP grade or both.</Note>}
      {blocks.length > 0 && <Note tone="red">Before approval:
        <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>{blocks.slice(0, 15).map((c, i) => <li key={i}>{c.room}: {c.message}</li>)}</ul></Note>}
      {err && <Note tone="red">{err}</Note>}
      {(!m.frozen || m.state !== "APPROVED") ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Button tone="green" disabled={busy || !m.approvable} onClick={() => act(`/projects/${pid}/room-master/approve`, {})}>
            Approve and freeze the requirements</Button>
          <span style={{ fontSize: 12, color: KZ.grey }}>The project's checker or approver — not the maker, and not whoever entered or accepted these requirements. Every extracted value must be decided first.</span>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "end", flexWrap: "wrap" }}>
          <label style={{ flex: 1, minWidth: 240 }}><span style={lbl}>Reason for a revision</span>
            <input value={why} onChange={e => setWhy(e.target.value)} placeholder="e.g. customer issued spec rev C" style={inp} /></label>
          <Button outline tone="grey" disabled={busy || !why.trim()} onClick={() => act(`/projects/${pid}/requirements/revise`, { reason: why })}>Open a revision</Button>
        </div>)}
    </div>
  );
}

// ── The project team (maker, checker, approver — Blueprint §8) ────────────────
export function TeamEditor({ api, authFetch, project, canEdit, onSaved }) {
  const [people, setPeople] = useState([]);
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({});
  const [err, setErr] = useState("");
  useEffect(() => { call(api, authFetch, "/project-team/candidates").then(setPeople).catch(() => { }); }, [api, authFetch]);
  if (!project) return null;
  const roles = [["responsible_engineer", "Maker"], ["checker", "Checker"], ["approver", "Approver"]];
  const who = (u) => { const m = people.find(x => x.username.toLowerCase() === (u || "").toLowerCase()); return m ? m.full_name : u; };
  const save = async () => {
    setErr("");
    try { const d = await call(api, authFetch, `/projects/${project.id}/team`, "PATCH", f); setEdit(false); onSaved && onSaved(d); }
    catch (e) { setErr(e.message); }
  };
  if (!edit) return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", fontSize: 12 }}>
      {roles.map(([k, l]) => <div key={k}><span style={{ color: KZ.grey, fontWeight: 700 }}>{l.toUpperCase()} </span>
        <span style={{ color: project[k] ? "#111827" : "#B45309" }}>{project[k] ? who(project[k]) : "not named"}</span></div>)}
      {roles.some(([k]) => !project[k]) && <span style={{ color: "#B45309" }}>Name all three — the checker or approver approves the requirements and releases calculations.</span>}
      <div style={{ flex: 1 }} />
      {canEdit && <Button outline onClick={() => { setF({ name: project.name, responsible_engineer: project.responsible_engineer || "", checker: project.checker || "", approver: project.approver || "" }); setEdit(true); }}>Edit name & team</Button>}
    </div>);
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
        <label><span style={lbl}>Project name</span><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} style={inp} /></label>
        {roles.map(([k, l]) => (
          <label key={k}><span style={lbl}>{l}</span>
            <select value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} style={inp}>
              <option value="">— not named —</option>
              {f[k] && !people.some(x => x.username.toLowerCase() === f[k].toLowerCase()) && <option value={f[k]}>{f[k]} (not a user)</option>}
              {people.map(x => <option key={x.username} value={x.username}>{x.full_name} ({x.username})</option>)}
            </select></label>))}
      </div>
      <div style={{ fontSize: 11, color: KZ.grey, margin: "6px 0" }}>Three different people. The maker records calculation runs; the checker or the approver approves requirements and releases runs.</div>
      {err && <Note tone="red">{err}</Note>}
      <div style={{ display: "flex", gap: 8 }}><Button onClick={save}>Save</Button><Button outline tone="grey" onClick={() => setEdit(false)}>Cancel</Button></div>
    </div>);
}

// ── 3 Design constants: fix / lock for this project ───────────────────────────
export function Constants({ api, authFetch, pid, canEdit, onChanged }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [ov, setOv] = useState(null);       // {key, value, reason, evidence}
  const [filter, setFilter] = useState("");
  const load = useCallback(() => call(api, authFetch, `/projects/${pid}/constants`).then(setS)
    .catch(e => setErr(e.message)), [api, authFetch, pid]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_project_constants", "eng_constants"], load);
  if (!s) return <div style={{ color: KZ.grey, fontSize: 13 }}>{err || "Loading…"}</div>;
  const act = async (path, method = "POST", body) => {
    setBusy(true); setErr("");
    try { setS(await call(api, authFetch, `/projects/${pid}/constants${path}`, method, body)); setOv(null); onChanged(); }
    catch (e) { setErr(e.message); }
    setBusy(false);
  };
  const rows = s.rows.filter(r => !filter || `${r.constant_id} ${r.parameter} ${r.engine}`.toLowerCase().includes(filter.toLowerCase()));
  const tone = { "PROJECT DESIGN BASIS": "#1E40AF", "PROJECT OVERRIDE": "#7C3AED", "LOCKED REGISTER": "#166534", "LIVE REGISTER": "#92400E" };
  return (
    <div>
      {s.state === "UNLOCKED" && <Note tone="grey">These are the register constants the project's scope ({s.scope.join(", ") || "not stated"}) calculates with. Locking fixes them at their current revision — a later register edit cannot move this project's figures until you re-lock.</Note>}
      {s.state === "LOCKED" && <Note tone="green">{s.summary} on {s.locked_at}.</Note>}
      {s.state === "OUTDATED" && <Note>The register has moved on since this project was locked. Its figures still use the locked values; re-lock to adopt:
        <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>{s.newer.map(n => <li key={n}>{n}</li>)}</ul></Note>}
      {s.warnings.length > 0 && <Note>{s.design_stage} design: {s.warnings.length} unapproved working constant(s) still govern —
        {" "}{s.warnings.slice(0, 4).join(" ")}{s.warnings.length > 4 ? " …" : ""}</Note>}
      {s.pending_overrides.length > 0 && <Note>Overrides awaiting the checker or approver: {s.pending_overrides.join(", ")} — they are not used until approved.</Note>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        {canEdit && <Button tone="green" disabled={busy} onClick={() => act("/lock")}>
          {s.state === "UNLOCKED" ? "Lock constants for this project" : "Re-lock at current register"}</Button>}
        {canEdit && s.state !== "UNLOCKED" && <Button outline tone="grey" disabled={busy}
          onClick={() => window.confirm("Unlock? The project goes back to the live register and its overrides are removed.") && act("/unlock")}>Unlock</Button>}
        <div style={{ flex: 1 }} />
        <input placeholder="Filter constants…" value={filter} onChange={e => setFilter(e.target.value)} style={{ ...inp, width: 220 }} />
      </div>
      {err && <Note tone="red">{err}</Note>}
      <div style={{ overflowX: "auto", maxHeight: 420, border: "1px solid #E5E7EB", borderRadius: 8 }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
            <tr>{["ID", "Parameter", "Engine", "Value", "Unit", "Status", "Stage", "Governed by", "Used", ""].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.key}>
              <td style={{ ...td, fontWeight: 600 }}>{r.constant_id || "—"}</td>
              <td style={{ ...td, whiteSpace: "normal", minWidth: 200 }}>{r.parameter}{r.newer ? <div style={{ fontSize: 11, color: "#92400E" }}>Register now {r.newer.revision}: {show(r.newer.value)}</div> : null}
                {r.override && <div style={{ fontSize: 11, color: "#7C3AED" }}>Override {show(r.override.value)} — {r.override.state.toLowerCase()} ({r.override.reason}; {r.override.evidence}; by {r.override.by}{r.override.decided_by ? `, ${r.override.state.toLowerCase()} by ${r.override.decided_by}` : ""})</div>}</td>
              <td style={td}>{r.engine || "—"}</td>
              {isTable(r.value)
                ? <td style={td}><StructuredValue value={r.value} unit={r.unit} /></td>
                : <td style={{ ...td, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }} title={show(r.value)}>{show(r.value)}</td>}
              <td style={td}>{r.unit || ""}</td>
              <td style={td}>{r.status}{r.approval_state && r.approval_state !== "APPROVED" ? <span style={{ color: KZ.grey }}> · {r.approval_state.toLowerCase()}</span> : null}</td>
              <td style={td}>{r.allowed_stage || "—"}</td>
              <td style={{ ...td, color: tone[r.governed_by], fontWeight: 600, fontSize: 11 }}>{r.governed_by}</td>
              {isTable(r.basis_value ?? r.used)
                ? <td style={td}><StructuredValue value={r.basis_value ?? r.used} unit={r.unit} /></td>
                : <td style={{ ...td, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }} title={show(r.basis_value ?? r.used)}>{show(r.basis_value ?? r.used)}</td>}
              <td style={td}>
                {canEdit && r.locked && r.override_policy !== "NO" && r.kind !== "lookup" &&
                  <button onClick={() => {
                    const v = r.override?.value ?? r.value;
                    setOv({ key: r.key, id: r.constant_id, unit: r.unit, base: isTable(r.value) ? r.value : null,
                            // a table override starts from the whole locked table, with any
                            // classes already overridden laid over it
                            value: isTable(v) ? { ...(isTable(r.value) ? r.value : {}), ...v } : v ?? "",
                            reason: "", evidence: "" });
                  }}
                    style={{ fontSize: 11.5, border: "none", background: "none", color: "#2563EB", cursor: "pointer" }}>Override…</button>}
                {r.override?.state === "PENDING" && <>
                  <button disabled={busy} onClick={() => act(`/${r.key}/override/decide`, "POST", { approve: true })}
                    style={{ fontSize: 11.5, border: "none", background: "none", color: "#059669", cursor: "pointer" }}>Approve</button>
                  <button disabled={busy} onClick={() => act(`/${r.key}/override/decide`, "POST", { approve: false })}
                    style={{ fontSize: 11.5, border: "none", background: "none", color: "#DC2626", cursor: "pointer" }}>Reject</button></>}
              </td>
            </tr>))}</tbody>
        </table>
      </div>
      {ov && (
        <div style={{ border: "1px solid #DDD6FE", background: "#FAF5FF", borderRadius: 8, padding: 12, marginTop: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Project override — {ov.id || ov.key}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            {!ov.base && <label><span style={lbl}>Value for this project</span>
              <input type="number" step="any" value={ov.value} onChange={e => setOv({ ...ov, value: e.target.value })} style={inp} /></label>}
            <label><span style={lbl}>Engineering reason</span>
              <input value={ov.reason} onChange={e => setOv({ ...ov, reason: e.target.value })} style={inp} /></label>
            <label><span style={lbl}>Evidence (URS / DBR / drawing / OEM doc)</span>
              <input value={ov.evidence} onChange={e => setOv({ ...ov, evidence: e.target.value })} style={inp} /></label>
          </div>
          {ov.base && <div style={{ marginTop: 10 }}><span style={lbl}>Values for this project</span>
            <KeyValueEditor value={ov.value} base={ov.base} baseLabel="Locked" unit={ov.unit} keys={Object.keys(ov.base)}
              onChange={v => setOv({ ...ov, value: v })} /></div>}
          <div style={{ fontSize: 11.5, color: KZ.grey, margin: "6px 0 8px" }}>The register is not changed. The override is used only after the project's checker or approver — not you — approves it.</div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={busy} onClick={() => {
              let value = ov.value;
              if (ov.base) {
                const p = parseKeyValues(ov.value);
                if (p.error) { setErr(p.error); return; }
                value = p.value;
              }
              act(`/${ov.key}/override`, "PUT", { value, reason: ov.reason, evidence: ov.evidence });
            }}>Propose override</Button>
            <Button outline tone="grey" onClick={() => setOv(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── The strip on the project page ─────────────────────────────────────────────
// The work is done on Engineering Flow; the project page says where the
// project stands and takes you to the stage.
export const FLOW_STAGE_KEY = "kz.flowStage";

export default function ProjectSetup({ api, authFetch, projectId, onGoTo }) {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => call(api, authFetch, `/projects/${projectId}/setup`).then(setSt)
    .catch(e => setErr(e.message)), [api, authFetch, projectId]);
  useEffect(() => { load(); }, [load]);
  useLive(["eng_projects", "eng_calc_runs", "eng_boq_lines", "eng_project_constants", "eng_requirement_values"], load);
  if (!st) return err ? <Note tone="red">{err}</Note> : null;
  const go = (key) => {
    setActiveProject(projectId);
    try { sessionStorage.setItem(FLOW_STAGE_KEY, key); } catch { /* storage blocked */ }
    onGoTo && onGoTo("Engineering Flow");
  };
  return (
    <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, padding: 14, marginBottom: 16, background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: KZ.blue }}>Engineering flow</div>
        <div style={{ fontSize: 12, color: KZ.grey }}>Click a stage to work on it.</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8 }}>
        {st.steps.map(s => {
          const [mark, fg, bg, br] = STATE[s.state] || STATE.TODO;
          return (
            <button key={s.key} onClick={() => go(s.key)}
              style={{ textAlign: "left", border: `1.5px solid ${st.next === s.key ? KZ.blue : br}`, background: bg,
                       borderRadius: 10, padding: "8px 10px", cursor: "pointer" }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: fg }}><span style={{ marginRight: 6 }}>{mark}</span>{s.n}. {s.title}</div>
              <div style={{ fontSize: 11.5, color: KZ.grey, marginTop: 3, lineHeight: 1.35 }}>{s.detail}</div>
            </button>);
        })}
      </div>
    </div>
  );
}
export { STATE as STAGE_STATE, Button, Note, call };
